/**
 * Receipt/slip designer block schema (README.md Phase 23) — the shape a
 * `ReceiptTemplate.schema` JSON column must satisfy: `{v:1, blocks:[...]}`,
 * an ordered list of typed, toggleable sections. Versioned so a future
 * layout change can add `v:2` and migrate/reject `v:1` docs explicitly,
 * rather than silently reinterpreting an old document under new rules.
 *
 * This is the first discriminated union in the codebase — each block's
 * `type` selects its shape, all extending a shared `visible` toggle every
 * block has regardless of kind.
 */
import { z } from 'zod';
import { idSchema } from './common.js';
export const RECEIPT_BLOCK_TYPES = [
    'logo',
    'shopHeader',
    'billMeta',
    'linesTable',
    'totals',
    'taxBreakdown',
    'payments',
    'loyalty',
    'barcodeQr',
    'footerText',
    'customText',
    'divider',
];
const baseBlock = {
    /** Client-local id — stable React key for reorder/edit, never inspected by the renderer. */
    id: z.string().min(1).max(64),
    visible: z.boolean().default(true),
};
const logoBlock = z.object({ type: z.literal('logo'), ...baseBlock });
const shopHeaderBlock = z.object({
    type: z.literal('shopHeader'),
    ...baseBlock,
    showAddress: z.boolean().default(true),
    showPhone: z.boolean().default(true),
});
const billMetaBlock = z.object({
    type: z.literal('billMeta'),
    ...baseBlock,
    showCashierName: z.boolean().default(true),
    showCustomer: z.boolean().default(true),
});
const linesTableBlock = z.object({
    type: z.literal('linesTable'),
    ...baseBlock,
    showItemDiscounts: z.boolean().default(true),
});
const totalsBlock = z.object({ type: z.literal('totals'), ...baseBlock });
const taxBreakdownBlock = z.object({ type: z.literal('taxBreakdown'), ...baseBlock });
const paymentsBlock = z.object({ type: z.literal('payments'), ...baseBlock });
const loyaltyBlock = z.object({ type: z.literal('loyalty'), ...baseBlock });
/** Text form only — no barcode/QR bitmap (README.md Phase 14's own stated gap, still unbuilt). */
const barcodeQrBlock = z.object({ type: z.literal('barcodeQr'), ...baseBlock });
const footerTextBlock = z.object({ type: z.literal('footerText'), ...baseBlock, text: z.string().max(300).default('') });
/**
 * Freeform text with placeholders — the one block where alignment is a real
 * choice; every other block already has an inherent layout the fixed-width
 * thermal grid dictates (headers centered, rows are label/value pairs).
 * Supported placeholders: {{shopName}} {{businessName}} {{billNo}} {{date}}
 * {{total}} {{customerName}}.
 */
const customTextBlock = z.object({
    type: z.literal('customText'),
    ...baseBlock,
    text: z.string().max(300).default(''),
    align: z.enum(['left', 'center', 'right']).default('center'),
});
const dividerBlock = z.object({ type: z.literal('divider'), ...baseBlock });
export const receiptBlockSchema = z.discriminatedUnion('type', [
    logoBlock,
    shopHeaderBlock,
    billMetaBlock,
    linesTableBlock,
    totalsBlock,
    taxBreakdownBlock,
    paymentsBlock,
    loyaltyBlock,
    barcodeQrBlock,
    footerTextBlock,
    customTextBlock,
    dividerBlock,
]);
export const receiptTemplateSchemaSchema = z.object({
    v: z.literal(1),
    blocks: z.array(receiptBlockSchema).min(1).max(40),
});
/** The system default, as a block list — every existing shop's receipt today, expressed the new way. Also what "Reset to default" restores and what a fresh template starts from. */
export const SYSTEM_DEFAULT_BLOCKS = {
    v: 1,
    blocks: [
        { id: 'logo', type: 'logo', visible: true },
        { id: 'shopHeader', type: 'shopHeader', visible: true, showAddress: true, showPhone: true },
        { id: 'billMeta', type: 'billMeta', visible: true, showCashierName: true, showCustomer: true },
        { id: 'linesTable', type: 'linesTable', visible: true, showItemDiscounts: true },
        { id: 'taxBreakdown', type: 'taxBreakdown', visible: true },
        { id: 'totals', type: 'totals', visible: true },
        { id: 'payments', type: 'payments', visible: true },
        { id: 'loyalty', type: 'loyalty', visible: true },
        { id: 'barcodeQr', type: 'barcodeQr', visible: true },
        { id: 'footerText', type: 'footerText', visible: true, text: '' },
    ],
};
/* -------------------------------------------------------------------------- */
/* CRUD input schemas                                                         */
/* -------------------------------------------------------------------------- */
export const PAPER_SIZES_FOR_TEMPLATE = ['MM58', 'MM80', 'A4', 'A5'];
export const createReceiptTemplateSchema = z.object({
    shopId: idSchema.nullable().optional(),
    name: z.string().min(1).max(120),
    paperSize: z.enum(PAPER_SIZES_FOR_TEMPLATE),
    schema: receiptTemplateSchemaSchema,
    isDefault: z.boolean().default(false),
});
export const updateReceiptTemplateSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    schema: receiptTemplateSchemaSchema.optional(),
    isDefault: z.boolean().optional(),
});
export const listReceiptTemplatesQuerySchema = z.object({
    shopId: idSchema.optional(),
    paperSize: z.enum(PAPER_SIZES_FOR_TEMPLATE).optional(),
});
/** What an exported `.json` file contains — re-validated on import, never trusted raw. */
export const receiptTemplateExportSchema = z.object({
    name: z.string().min(1).max(120),
    paperSize: z.enum(PAPER_SIZES_FOR_TEMPLATE),
    schema: receiptTemplateSchemaSchema,
});
//# sourceMappingURL=receipt-blocks.js.map